import { Box } from "@mui/material";
import { useEffect, useMemo, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import AgGrid from "../components/grid/AgGrid";
import { defaultColDef, floatFormater, leftAlignColDef, preciseFloatFormater, rightAlignColDef } from "../components/grid/columnDefinitions/columnDefinitions";
import { useContextProvider } from "../components/page-context/ContextProvider";
import { Circularity } from "../db_access/circularity";
import { calculate_im_mass_virgin_material, calculate_im_virgin_content, recalculate_row_mci } from "../functions/CircularityFunctions";
import ColumnMenu from "../components/context-menu/ColumnMenu";

function InputMaterialTable({ circularities, columnMenuVisible, setColumnMenuVisible, anchorRef, setCircularities }) {
  const { isSaveClicked, setIsSaveVisible, setRefresh, setIsLoading, setIsSaveClicked, columnVisibilities, enqueueAlert } = useContextProvider();
  const { t } = useTranslation();
  const [inputMaterials, setInputMaterials] = useState([]);
  const [modified, setModified] = useState(false);
  const gridRef = useRef(null);

  const colVisibility_key = "circularity_input_material_table_visibility";

  const rawColDefs = [
    {
      headerName: "SI No.",
      valueGetter: "node.rowIndex + 1",
      width: 80,
      minWidth: 60,
      headerClass: "ag-center-aligned-header",
      cellStyle: { display: "flex", alignItems: "center", justifyContent: "center" },
      suppressMenu: true,
      sortable: false,
      field: "si_no"
    },
    { field: "description", headerName: "Description", ...leftAlignColDef, ...defaultColDef, flex: 1 },
    { field: "im_recycled_content", headerName: "Recycled content (%)", ...rightAlignColDef, ...defaultColDef, ...floatFormater, editable: true },
    { field: "im_reuse", headerName: "Reuse (%)", ...rightAlignColDef, ...defaultColDef, ...floatFormater, editable: true },
    { field: "im_remanufactured", headerName: "Remanufactured (%)", ...rightAlignColDef, ...defaultColDef, ...floatFormater, editable: true },
    { field: "im_bio_materials", headerName: "Biological materials (%)", ...rightAlignColDef, ...defaultColDef, ...floatFormater, editable: true },
    { field: "im_virgin_content", headerName: "Virgin content (%)", ...rightAlignColDef, ...defaultColDef, ...floatFormater },
    { field: "im_mass_virgin_material", headerName: "Mass of Virgin material (%)", ...rightAlignColDef, ...defaultColDef, ...floatFormater },


  ];

  // Hide Biogenic columns by default
  const defaultCols = useMemo(() => rawColDefs
    .filter(c => c.field !== "im_bio_materials")
    .map((c) => c.field), 
  []);

  const columnDefs = useMemo(() => {
    const isHidden = (field) => {
      if (field === "si_no") return false;
      return !columnVisibilities[colVisibility_key]?.includes(field);
    };
    return rawColDefs.map((col) => ({
      ...col,
      hide: isHidden(col.field),
    }));
  }, [columnVisibilities, colVisibility_key]);

  useEffect(() => {
    if (!modified && !isSaveClicked) {
      setInputMaterials([...circularities]);
    }
  }, [circularities, modified, isSaveClicked]);



  useEffect(() => {
    if (isSaveClicked && modified) {
      const payload = inputMaterials.map((item) => ({ ...item, id: Number(item.id) }));
      Circularity.update_input_materials(payload).finally(() => {
        setModified(false);
        // Parent handles setIsLoading and setIsSaveClicked after the 2s MCI sync
      });

    }
  }, [isSaveClicked]);

  useEffect(() => {
    if (modified) {
      setIsSaveVisible(true);
    }
  }, [modified]);

  const onCellValueChanged = (params) => {
    const { data, colDef, value, oldValue } = params;
    let validatedValue = value;
    if (["im_recycled_content", "im_reuse", "im_remanufactured", "im_bio_materials"].includes(colDef.field)) {
      const parsed = parseFloat(value);
      if (isNaN(parsed) || parsed < 0 || parsed > 100) {
        if (enqueueAlert) {
          enqueueAlert({
            message: t("Value must be between 0 and 100."),
            severity: "warning",
          });
        }
        params.node.setDataValue(colDef.field, oldValue !== undefined ? oldValue : 0);
        return;
      }

      const temp_im = { ...data, [colDef.field]: parsed };
      const irc = parseFloat(temp_im.im_recycled_content) || 0;
      const iru = parseFloat(temp_im.im_reuse) || 0;
      const irm = parseFloat(temp_im.im_remanufactured) || 0;
      const ibm = parseFloat(temp_im.im_bio_materials) || 0;
      if (irc + iru + irm + ibm > 100) {
        if (enqueueAlert) {
          enqueueAlert({
            message: t("The sum of Recycled content, Reuse, Remanufactured, and Biological materials cannot exceed 100%."),
            severity: "warning",
          });
        }
        params.node.setDataValue(colDef.field, oldValue !== undefined ? oldValue : 0);
        return;
      }
      validatedValue = parsed;
    }
    const updated = inputMaterials.map((im) => {
      if (im.id == data.id) {
        const new_im = { ...data, [colDef.field]: validatedValue };
        return recalculate_row_mci(new_im);
      } else {
        return im;
      }
    });
    setInputMaterials(updated);
    setModified(true);
    // Notify parent for live summary updates
    if (setCircularities) {
      setCircularities(updated);
    }
  };


  return (
    <Box sx={{ height: "auto" }}>
      <AgGrid 
        ref={gridRef}
        columnDefs={columnDefs} 
        rowData={inputMaterials} 
        onCellValueChanged={onCellValueChanged} 
        headerCheckbox={false} 
      />
      <ColumnMenu
        anchorRef={anchorRef}
        visible={columnMenuVisible}
        onClose={() => setColumnMenuVisible(false)}
        colDefs={rawColDefs}
        colVisibility_key={colVisibility_key}
        defaultCols={defaultCols}
        gridRef={gridRef}
      />
    </Box>
  );
}

export default InputMaterialTable;

